import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { GET_TEAM_MEMBER_PTO_HISTORY } from '../../graphql/team-availability-queries';
import { DELETE_EMPLOYEE_VACATION } from '../../graphql/team-availability-mutations';
import { toLocalCalendarDate } from '../../utils/availabilityDateUtils';
import AdminUserTimeOffFormModal from './admin-edit/AdminUserTimeOffFormModal';

const IMPACT_STYLES = {
  USED: { label: 'Counted as used', className: 'border-emerald-200 bg-emerald-50 text-emerald-800' },
  SCHEDULED: { label: 'Future — not yet used', className: 'border-sky-200 bg-sky-50 text-sky-800' },
  TYPICAL_DAY_OFF: { label: 'Regular day off — not counted', className: 'border-gray-200 bg-gray-50 text-gray-700' },
  SCHEDULE_NOT_SET: { label: 'Schedule not set — not counted', className: 'border-amber-200 bg-amber-50 text-amber-800' },
  PENDING: { label: 'Pending — not counted', className: 'border-amber-200 bg-amber-50 text-amber-800' },
  DENIED: { label: 'Denied — not counted', className: 'border-red-200 bg-red-50 text-red-800' },
  DUPLICATE: { label: 'Duplicate date — counted once', className: 'border-violet-200 bg-violet-50 text-violet-800' },
};

const REQUEST_STYLES = {
  APPROVED: 'bg-emerald-100 text-emerald-800',
  PENDING: 'bg-amber-100 text-amber-800',
  DENIED: 'bg-red-100 text-red-800',
};

const formatDate = (value, pattern = 'EEE, MMM d, yyyy') => (
  format(toLocalCalendarDate(value), pattern)
);

const formatTimestampDate = (value) => format(new Date(value), 'MMM d, yyyy');

function PtoHistoryPanel({ isOpen, user, onClose, onChange }) {
  const [editingRequest, setEditingRequest] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const { data, loading, error, refetch } = useQuery(GET_TEAM_MEMBER_PTO_HISTORY, {
    variables: { userId: user?.id },
    skip: !isOpen || !user?.id,
    fetchPolicy: 'network-only',
  });
  const [deleteEmployeeVacation] = useMutation(DELETE_EMPLOYEE_VACATION, {
    onCompleted: async () => {
      toast.success('PTO entry deleted');
      setDeletingId(null);
      await refetch();
      onChange?.();
    },
    onError: (mutationError) => {
      toast.error(mutationError.message || 'Failed to delete PTO entry');
      setDeletingId(null);
    },
  });

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) onClose?.();
    };
    document.addEventListener('keydown', handleEscape);
    if (isOpen) document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!user) return null;

  const history = data?.teamMemberPtoHistory;
  const currentYear = new Date().getFullYear();
  const currentYearData = history?.years?.find((year) => year.year === currentYear);
  const daysUsedThisYear = currentYearData?.daysUsed || 0;

  const handleEdit = (request) => {
    setEditingRequest({ ...request, vacationType: 'PTO' });
  };

  const handleDelete = (request) => {
    const dateLabel = request.startDate === request.endDate
      ? formatDate(request.startDate)
      : `${formatDate(request.startDate)} – ${formatDate(request.endDate)}`;
    const confirmed = window.confirm(
      `Permanently delete the PTO entry for ${dateLabel}? This changes the employee's PTO history and balance and cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingId(request.id);
    deleteEmployeeVacation({ variables: { id: request.id } });
  };

  const handleEditSaved = async () => {
    setEditingRequest(null);
    await refetch();
    onChange?.();
  };

  return (
    <>
      <button
        type="button"
        aria-label="Close PTO history"
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-black transition-opacity duration-300 ${
          isOpen ? 'pointer-events-auto bg-opacity-50' : 'pointer-events-none bg-opacity-0'
        }`}
      />
      <aside
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-3xl transform bg-gray-50 shadow-2xl transition-transform duration-[440ms] ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={`PTO history for ${user.name}`}
      >
        <div className="flex h-full flex-col">
          <header className="flex items-start justify-between gap-4 border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">PTO history audit</p>
              <h2 className="truncate text-xl font-semibold text-gray-900">{user.name}</h2>
              <p className="mt-1 text-sm text-gray-500">Yearly totals and the exact dates used by the balance calculation</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
              aria-label="Close PTO history"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {loading ? (
              <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-gray-500">Loading PTO history...</div>
            ) : error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-700">
                Unable to load PTO history: {error.message}
              </div>
            ) : history ? (
              <div className="space-y-5">
                <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="rounded-lg bg-gray-50 p-3">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{currentYear} allowance</p>
                      <p className="mt-1 text-2xl font-semibold text-gray-900">{history.currentAnnualAllowance}</p>
                    </div>
                    <div className="rounded-lg bg-emerald-50 p-3">
                      <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Used to date</p>
                      <p className="mt-1 text-2xl font-semibold text-emerald-900">{daysUsedThisYear}</p>
                    </div>
                    <div className="rounded-lg bg-violet-50 p-3">
                      <p className="text-xs font-medium uppercase tracking-wide text-violet-700">Remaining</p>
                      <p className="mt-1 text-2xl font-semibold text-violet-900">{history.currentYearRemaining}</p>
                    </div>
                  </div>
                  <p className="mt-3 text-xs leading-5 text-gray-500">
                    The app stores the current allowance and recurring schedule, not a history of changes to either setting. Prior years show recorded PTO requests, with date classifications based on the team member's current recurring schedule.
                  </p>
                </section>

                {history.years.map((year) => (
                  <section key={year.year} className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                    <div className="flex flex-col gap-3 border-b border-gray-200 bg-gray-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">{year.year}</h3>
                      <div className="flex flex-wrap gap-2 text-xs font-medium">
                        <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-800">{year.daysUsed} used</span>
                        <span className="rounded-full bg-sky-100 px-2.5 py-1 text-sky-800">{year.daysScheduled} scheduled</span>
                        <span className="rounded-full bg-gray-200 px-2.5 py-1 text-gray-700">{year.requests.length} requests</span>
                      </div>
                    </div>

                    {year.requests.length === 0 ? (
                      <div className="p-6 text-center text-sm text-gray-500">No PTO requests recorded for this year.</div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {year.requests.map((request) => (
                          <article key={`${year.year}-${request.id}`} className="p-4">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                              <div>
                                <p className="font-medium text-gray-900">
                                  {formatDate(request.startDate)}
                                  {request.startDate !== request.endDate && ` – ${formatDate(request.endDate)}`}
                                </p>
                                <p className="mt-1 text-xs text-gray-500">
                                  Requested {formatTimestampDate(request.createdAt)}
                                  {request.reviewedAt && ` · Reviewed ${formatTimestampDate(request.reviewedAt)}`}
                                </p>
                              </div>
                              <div className="flex flex-wrap items-center gap-2 self-start">
                                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                                  REQUEST_STYLES[request.approvalStatus] || 'bg-gray-100 text-gray-700'
                                }`}>
                                  {request.approvalStatus}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleEdit(request)}
                                  className="rounded-md border border-sky-200 px-2.5 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-50"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(request)}
                                  disabled={deletingId !== null}
                                  className="rounded-md border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {deletingId === request.id ? 'Deleting...' : 'Delete'}
                                </button>
                              </div>
                            </div>
                            {request.reason && <p className="mt-2 text-sm text-gray-600">{request.reason}</p>}
                            <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                              {request.dates.map((entry) => {
                                const impact = IMPACT_STYLES[entry.balanceImpact] || {
                                  label: entry.balanceImpact,
                                  className: 'border-gray-200 bg-gray-50 text-gray-700',
                                };
                                return (
                                  <li
                                    key={`${request.id}-${entry.date}`}
                                    className={`rounded-md border px-3 py-2 ${impact.className}`}
                                  >
                                    <p className="text-sm font-semibold">{formatDate(entry.date)}</p>
                                    <p className="mt-0.5 text-xs">{impact.label}</p>
                                  </li>
                                );
                              })}
                            </ul>
                          </article>
                        ))}
                      </div>
                    )}
                  </section>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </aside>

      <AdminUserTimeOffFormModal
        isOpen={Boolean(editingRequest)}
        onClose={() => setEditingRequest(null)}
        userId={user.id}
        vacation={editingRequest}
        onSaved={handleEditSaved}
      />
    </>
  );
}

export default PtoHistoryPanel;
